import React, { useState, useEffect, memo, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { CircularProgress } from '@material-ui/core'

import {
  WebRounded as PanelIcon,
  RefreshRounded as LaunchIcon,
} from '@material-ui/icons'

import Tooltip from '../../atoms/display/tooltip'
import IconButton from '../../atoms/inputs/iconButton'
import Icon from '../../atoms/display/icon'

import { getChatInfo } from '../../../utils/chatInfo'

import useStyles from './customWidgetStyle'
import api from '../../../services/api'

const WidgetIframe = ({ data, chatId, setWidgetsList, ...props }) => {
  const { t } = useTranslation()
  const classes = useStyles()
  const refIframe = useRef(null)
  const timeOut = useRef(null)
  const refTimeOut = useRef(null)

  const generalInformation = useSelector((state) => state.generalInformation)
  const agentData = useSelector((state) => state.user)
  const profileData = useSelector((state) => state.profiles[0])
  const dispatch = useDispatch()
  const conversations = generalInformation?.conversations

  const [isLoading, setIsLoading] = useState(true)
  const [alreadySentPostM, setAlreadySentPostM] = useState(!data?.postMessage)

  async function generatePayload(consumerInfo) {
    const token = agentData?.AuthorizationRA?.replace('Bearer ', '') || ''
    const response = await api({
      method: 'GET',
      url: '/token-erp',
    })
    const tokenERP = response?.data?.data?.token
    if (!tokenERP) {
      throw new Error('Get Token ERP Error')
    }

    const payload = {
      token,
      consumer: {
        cpf: consumerInfo?.document || '',
      },
      agent: {
        profile: {
          profile_name: profileData?.name,
          profile_id: profileData?.id,
        },
        agentData: {
          user_id: agentData?.id,
          user_name: agentData?.name,
          extra_matricula: agentData?.extra_matricula,
        },
      },
      authToken: tokenERP,
    }
    return payload
  }

  async function sendPostMessage() {
    if (refIframe.current && data?.postMessage) {
      const { consumerInfo } = conversations[chatId]
      const payload = await generatePayload(consumerInfo)
      refIframe.current.contentWindow.postMessage(
        {
          payload,
          sender: 'customer',
        },
        data?.url
      )
      setAlreadySentPostM(true)
    }
  }

  function unMount() {
    setWidgetsList((prev) => ({
      ...prev,
      [chatId]: {
        unMount: true,
      },
    }))
  }

  const handleRefresh = () => {
    if (refIframe.current) {
      refIframe.current.src = data?.url
      setIsLoading(true)
      setAlreadySentPostM(!data?.postMessage)
    }
  }

  useEffect(() => {
    if (
      chatId &&
      !conversations[chatId] &&
      !generalInformation?.status?.get_general_info?.loading
    ) {
      getChatInfo(chatId, dispatch)
    }
  }, [chatId])

  useEffect(() => {
    if (
      data?.postMessage &&
      !isLoading &&
      conversations[chatId] &&
      !alreadySentPostM
    ) {
      sendPostMessage()
    }
  }, [isLoading, conversations])

  useEffect(() => {
    if (props?.isOpen) {
      if (timeOut.current) {
        clearTimeout(timeOut.current)
      }
    } else {
      timeOut.current = setTimeout(() => {
        unMount()
      }, 30 * 1000 * 60)
      return () => {
        clearTimeout(timeOut.current)
      }
    }
  }, [props?.isOpen])

  return (
    <>
      <div
        className={`${classes.root} ${
          props?.isOpen ? classes.visible : classes.hidden
        }`}
        ref={refTimeOut}
        key={props?.key}
      >
        {!data?.full && (
          <div className={classes.header}>
            <Icon text={data?.title || t('commons.link')}>
              <PanelIcon />
            </Icon>
            <div style={{ width: 10 }} />
            <Tooltip placement="bottom" title={t('commons.refresh')}>
              <div>
                <IconButton color="primary" onClick={() => handleRefresh()}>
                  <LaunchIcon className={classes.headerIcon} />
                </IconButton>
              </div>
            </Tooltip>
          </div>
        )}
        <div className={classes.content}>
          {isLoading && (
            <div className={classes.loading}>
              <CircularProgress size={50} />
            </div>
          )}
          <iframe
            style={data?.style || { width: 1200, height: '100%' }}
            className={classes.content}
            src={data?.url}
            frameBorder="0"
            allow="clipboard-write"
            ref={refIframe}
            onLoad={() => {
              setIsLoading(false)
            }}
          />
        </div>
      </div>
    </>
  )
}

export default memo(WidgetIframe)
